
import { useState } from 'react';
import { MapPin, Radio, Clock, X, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CastResponseModal } from './CastResponseModal';
import { formatBroadcastRadius } from '@/utils/distanceUtils';
import { formatLocationForDisplay } from '@/utils/locationUtils';
import { formatRelativeSafe } from '@/utils/date';
import type { Cast } from '@/hooks/useCasts';

interface IsoCastAdPlacementProps {
  cast: Cast;
  onClose?: () => void;
}

export const IsoCastAdPlacement = ({ cast, onClose }: IsoCastAdPlacementProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [showResponseModal, setShowResponseModal] = useState(false);

  // Debug the cast structure
  console.log('IsoCastAdPlacement cast:', cast);
  console.log('IsoCastAdPlacement cast.user:', cast?.user);
  
  if (!cast) {
    console.warn('IsoCastAdPlacement: cast is undefined');
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleRespond = () => {
    setShowResponseModal(true);
  };

  if (!isVisible) return null;

  const getPriorityColor = (sentiment?: string) => {
    switch (sentiment) {
      case "help":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "px-2 py-0.5 text-xs rounded-full border-iso-soft-border bg-iso-soft-bg text-[#3E1C8A]";
    }
  };

  const getPriorityLabel = (sentiment?: string) => {
    switch (sentiment) {
      case "help":
        return "Urgent";
      default:
        return "General";
    }
  };

  return (
    <>
      <div className="bg-gradient-to-r from-iso-soft-bg to-purple-100 border-2 border-iso-soft-border rounded-lg overflow-hidden shadow-sm">
        {/* Compact Ad Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-iso-soft-bg border-b border-iso-soft-border">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-iso-soft-bg text-[#3E1C8A] text-xs px-1.5 py-0.5 border border-iso-soft-border">
              Promoted ISO Cast
            </Badge>
            <Radio className="text-iso-solid" size={12} />
          </div>
            <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-5 w-5 p-0 text-iso-solid hover:text-violet-800 hover:bg-iso-soft-bg"
          >
            <X size={12} />
          </Button>
        </div>

        {/* Compact Cast Content */}
        <div className="p-3">
          {/* Compact User Info */}
          <div className="flex items-start space-x-2 mb-3">
            <Avatar className="w-8 h-8">
              <AvatarImage src={cast.user?.avatar || ''} alt={cast.user?.name || 'User'} />
              <AvatarFallback className="text-xs">{cast.user?.name?.[0] || 'U'}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-ink-primary text-sm truncate">{cast.user?.name || 'Unknown User'}</h3>
                {cast.user?.verified && (
                  <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
                <span className="text-ink-meta text-xs truncate">@{cast.user?.username || 'unknown'}</span>
                <Badge className={`text-xs px-1.5 py-0.5 ${getPriorityColor(cast.sentiment)}`}>
                  {getPriorityLabel(cast.sentiment)}
                </Badge>
              </div>
              <div className="flex items-center space-x-1 text-xs text-ink-meta mt-0.5">
                <MapPin size={10} />
                <span className="truncate">{formatLocationForDisplay(cast.location_address, "Location available")}</span>
                <span>•</span>
                <Clock size={10} />
                <span className="flex-shrink-0">{formatRelativeSafe(cast.created_at as any)}</span>
              </div>
            </div>
          </div>

          {/* Compact ISO Request */}
          <div className="mb-3">
            <div className="rounded-md p-2 border-l-2 bg-iso-soft-bg border-iso-soft-border">
              <h4 className="font-medium text-ink-primary text-xs mb-1">Looking for:</h4>
              <p className="text-ink-body text-sm">{cast.iso_request}</p>
            </div>
          </div>

          {/* Compact Location Info */}
          <div className="mb-3 p-2 bg-iso-soft-bg rounded-md border border-iso-soft-border">
            <div className="flex items-center gap-1 text-xs text-[#3E1C8A]">
              <MapPin size={10} />
              <span>Broadcasting within {formatBroadcastRadius(cast.location_radius_km)}</span>
            </div>
          </div>

          {/* Compact Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-iso-soft-border">
            <div className="flex items-center space-x-3 text-xs text-ink-meta">
              <div className="flex items-center gap-1">
                <MessageCircle size={12} />
                <span>{cast.comments_count} responses</span>
              </div>
            </div>
            <Button 
              onClick={handleRespond}
              className="bg-iso-solid hover:bg-violet-700 text-white text-xs h-7 px-3"
            >
              Help Out
            </Button>
          </div>
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && (
        <CastResponseModal
          cast={cast}
          onClose={() => setShowResponseModal(false)}
        />
      )}
    </>
  );
};
